import { FC, ReactNode, useMemo, useContext, createContext, useEffect, useState, useCallback } from 'react';
import posthog, { PostHog } from 'posthog-js';
import * as Sentry from '@sentry/react';
import { AxiosResponse } from 'axios';
import jwtDecode from 'jwt-decode';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';

import { isJWTExpired, hasNoPermission } from '@global-contexts/utils/authUtils';
import { useToast } from '@global-contexts/toast';

import userAPI from '@global-apis/user';
import authAPI from '@global-apis/auth';
import companyAPI from '@global-apis/company';
import generalAPI, { GeneralData } from '@global-apis/general';
import impersonationAPI from '@global-apis/impersonation';

import { ADMIN, MANAGER } from '@global-utils/defaultValues';

import { ALLOWED_THIRD_APP } from '@root/constants';

import { useCostCodesWithParent } from '@global-hooks/costCode';
import { useActiveProjectList } from '@global-hooks/project';
import { useActiveUserList, useUpdateUser } from '@global-hooks/users';
import { useVendorList } from '@global-hooks/vendor';
import { useFeaturebase } from '@global-hooks/useFeaturebase';
import { deleteWebSocket } from '@global-hooks/webSocket';

import { useMe, useGroups, useGeneralSettings, useUpdateCurrentUser, useUpdateProfile } from '@global-hooks/auth';

import { queryClient } from '@global-utils/queryClient';
import { persister } from '@global-utils/persister';
import {
  SESSION_EVENT,
  advanceSessionGeneration,
  deferRenewalDuringLogout,
  isCurrentSession,
  isSessionRecovering,
  isSessionRenewing,
  renewSession,
  sessionSnapshot
} from '@global-utils/sessionRenewal';
import {
  ImpersonationSession,
  beginImpersonation,
  getImpersonationSession,
  impersonationHasExpired,
  restoreOperatorSession
} from '@global-utils/impersonation';

import User, { UserGroup } from '@global-interfaces/User';
import Company, { CompanyIntegrations } from '@global-interfaces/Company';
import Address from '@global-interfaces/Address';

declare global {
  interface Window {
    // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
    dataLayer: any[];
  }
}
export interface AuthContextType {
  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  generalSettings: any;

  isSidebarCondensed: boolean;
  setIsSidebarCondensed: (condensed: boolean) => void;

  user: User | null;
  userNavigator: { isMacOs: boolean };
  groups: UserGroup[];
  loading: boolean;
  started: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  error: any;
  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  setError: (error: any) => void;

  authTokenRefreshChecking: boolean;

  company: Partial<CompanyAuth>;
  fetchCompanyData: () => Promise<void>;
  switchCompany: (company: number) => Promise<void>;
  token: string | null;
  refreshToken: string | null;
  updateToken: () => void;

  isAuthenticated: () => boolean;
  loginAfterMfa: (access: string, refresh: string) => void;
  logout: () => void;

  impersonation: ImpersonationSession | null;
  startImpersonation: (userId: number, reason: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;

  createUser: (userDetails: Partial<User>) => Promise<AxiosResponse<User, Error>>;
  updateUser: (id: number, data: Partial<User>) => Promise<void | AxiosResponse<User>>;
  refetchUserInfo: () => void;
  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  updatePassword: (id: any, data: any) => void;

  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  updateProfile: (id: any, data: any) => void;
  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  updateCurrentUser: (id: any, data: any) => void;

  integrationAvailable: Partial<CompanyIntegrations>;
}

export interface CompanyAuth extends Omit<Company, 'address'>, Omit<Address, 'id'> {
  address?: string;
}

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

// A token that cannot be decoded counts as needing renewal, so renewSession decides what to do with it.
const tokenNeedsRenewal = (accessToken: string) => {
  try {
    return isJWTExpired(accessToken).accessTokenWillExpireWithinBufferTime;
  } catch {
    return true;
  }
};

const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { toast } = useToast();

  const userNavigator = useMemo(() => {
    // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
    const platform = (navigator as any)?.userAgentData?.platform || navigator?.platform || 'unknown';

    const isMacOs = platform.toLowerCase().includes('mac');

    return {
      isMacOs
    };
  }, []);

  const [token, setToken] = useState<string | null>(() => localStorage.getItem('__token'));
  const [refreshToken, setRefreshToken] = useState<string | null>(() => localStorage.getItem('__refresh-token'));
  const [authTokenBeingRefreshed, setAuthTokenBeingRefreshed] = useState(false);
  const [sessionRecovering, setSessionRecovering] = useState(false);
  const [authTokenRefreshChecking, setAuthTokenRefreshChecking] = useState(false);

  const isAuthenticated = useCallback(() => {
    if (localStorage.getItem('__token') && (authTokenBeingRefreshed || isSessionRenewing() || sessionRecovering)) {
      return true;
    }
    const cacheToken = localStorage.getItem('__token');
    if (!cacheToken) {
      return false;
    }

    const { accessTokenIsExpired } = isJWTExpired(cacheToken);

    if (accessTokenIsExpired) {
      return false;
    }

    return true;
  }, [authTokenBeingRefreshed, sessionRecovering]);

  const authenticated = isAuthenticated();

  const {
    data: userData,
    isLoading: isLoadingMe,
    refetch: refetchMe
  } = useMe({
    enabled: authenticated && !authTokenBeingRefreshed && !sessionRecovering
  });

  const { data: groupsData, isLoading: isLoadingGroups } = useGroups({
    enabled: authenticated && !authTokenBeingRefreshed && !sessionRecovering
  });

  const { data: generalSettings, isLoading: isLoadingSettings } = useGeneralSettings({
    enabled: authenticated && !authTokenBeingRefreshed && !sessionRecovering
  });

  const updateCurrentUserMutation = useUpdateCurrentUser();
  const updateProfileMutation = useUpdateProfile();
  const updateUserMutation = useUpdateUser();

  const [company, setCompany] = useState<Partial<CompanyAuth>>({});
  const [error, setError] = useState(null);
  const [loadedPostHog, setLoadedPostHog] = useState<null | PostHog>(null);

  const [shouldFetchData, setShouldFetchData] = useState(false);

  useCostCodesWithParent({ enabled: shouldFetchData });
  useActiveProjectList({ enabled: shouldFetchData });
  useVendorList({ enabled: shouldFetchData });

  useActiveUserList({
    enabled: shouldFetchData
  });

  const { reset: resetFeaturebase, update: updateFeaturebase } = useFeaturebase();

  // remove this when we have a better solution for the sidebar or when we remove the sidebar
  const [isSidebarCondensed, setIsSidebarCondensed] = useState(true);

  const loading = isLoadingMe || isLoadingGroups || isLoadingSettings;

  const user = useMemo(() => {
    if (!userData || !groupsData) {
      return null;
    }

    const permissions = userData.groups.map(
      (userGroup) => groupsData.find((group) => group.id === userGroup)?.name ?? ''
    );

    return {
      ...userData,
      full_name: `${userData.first_name} ${userData.last_name}`,
      permissions,
      isAdmin: permissions.includes(ADMIN),
      isManager: permissions.includes(MANAGER)
    } as User;
  }, [userData, groupsData]);

  const started = useMemo(() => {
    return Boolean(!loading && user);
  }, [loading, user]);

  useEffect(() => {
    if (generalSettings?.posthog_enabled) {
      posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        // biome-ignore format:  no break line
        ...(ALLOWED_THIRD_APP === false && { autocapture: false, debug: false, disable_session_recording: true, capture_performance: false }),
        session_recording: { maskAllInputs: true, maskInputOptions: { password: true } },
        loaded: (postHogLoaded) => {
          setLoadedPostHog(postHogLoaded);
        }
      });
    }
  }, [generalSettings]);

  useEffect(() => {
    if (user && loadedPostHog) {
      loadedPostHog.identify(user.id.toString(), {
        name: user.full_name,
        email: user.email,
        company_id: user.profile.company?.id.toString(),
        company_name: user.profile.company?.name
      });
    }
  }, [loadedPostHog, user]);

  useEffect(() => {
    if (!authenticated || !user) {
      resetFeaturebase();
      return;
    }

    updateFeaturebase(user);
  }, [authenticated, resetFeaturebase, updateFeaturebase, user]);

  useEffect(() => {
    if (userData) {
      window.dataLayer?.push?.({
        company_name: userData.profile?.company?.name,
        company_id: userData.profile?.company?.id,
        user_name: userData.email,
        user_id: userData.id,
        event: 'login-btn'
      });

      Sentry.getCurrentScope()?.setTag('company', userData.profile?.company?.name);
      Sentry.getCurrentScope()?.setUser({
        id: userData.id,
        email: userData.email,
        company_id: userData.profile?.company?.id,
        company_name: userData.profile?.company?.name
      });

      const foundCompany = userData.profile?.company;

      if (foundCompany) {
        localStorage.setItem('__company_id', foundCompany.id.toString());
        localStorage.setItem('__company_name', foundCompany.name);
        localStorage.setItem('__user_id', userData.id.toString());
        localStorage.setItem('__user_email', userData.email);

        window.dispatchEvent(new Event('user-localstorage-changed'));

        setCompany({
          ...foundCompany,
          zip_code: foundCompany.address?.zip_code ?? '',
          address: foundCompany.address?.address_1 ?? '',
          address_1: foundCompany.address?.address_1 ?? '',
          address_2: foundCompany.address?.address_2 ?? '',
          city: foundCompany.address?.city ?? '',
          state: foundCompany.address?.state ?? '',
          phone: foundCompany.address?.phone ?? '',
          country: foundCompany.address?.country ?? null,
          website: foundCompany.website,
          email: foundCompany.email,
          ein: foundCompany.ein,
          logo: foundCompany.logo,
          partner_logo: foundCompany.partner_logo,
          company_flags: foundCompany.company_flags ?? {},
          integration_data: foundCompany.integration || foundCompany.integration_data,
          integrations_available: Object.assign(
            {
              balance_available: false,
              docusign_available: false,
              dropbox_available: false,
              gmail_available: false,
              outlook_available: false,
              procore_available: false,
              procore_timecard_available: false,
              quickbooks_available: false,
              quickbooks_desktop_available: false,
              quickbooks_desktop_mac_available: false,
              quickbooks_desktop_time_available: false,
              quickbooks_time_available: false,
              sage_available: false
            },
            foundCompany.integration || foundCompany.integrations_available,
            { procore_available: false, procore_timecard_available: false }
          )
        });
      }
    }
  }, [userData]);

  const refreshTokenIfNeeded = async () => {
    const accessTokenFromLS = localStorage.getItem('__token');

    if (!accessTokenFromLS) {
      return;
    }

    // An impersonation token is deliberately non-renewable and has no refresh token,
    // so the normal path would fail its refresh and log the operator out of their own
    // account. Drop back to their session instead when it runs out.
    const activeImpersonation = getImpersonationSession();
    if (activeImpersonation) {
      if (impersonationHasExpired(activeImpersonation)) {
        await stopImpersonation();
      }
      return;
    }

    if (window.location.pathname === '/login') {
      return;
    }

    // Nothing to renew: skip the state flips below, which re-render every useAuth consumer
    // twice per tick and would remount any component that is unstable across renders.
    // Reads localStorage on purpose, so a token renewed by another tab counts as fresh.
    if (!tokenNeedsRenewal(accessTokenFromLS) && !isSessionRecovering() && !isSessionRenewing()) {
      return;
    }
    setAuthTokenBeingRefreshed(true);
    try {
      await renewSession(false, authAPI.refreshToken);
      setToken(localStorage.getItem('__token'));
      setRefreshToken(localStorage.getItem('__refresh-token'));
      setSessionRecovering(isSessionRecovering());
    } finally {
      setAuthTokenBeingRefreshed(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: safe to ignore
  useEffect(() => {
    setAuthTokenRefreshChecking(true);
    // Refresh token on app initialization. E.g when user refreshes the page or back to the app after a while/long time
    refreshTokenIfNeeded();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: safe to ignore
  useEffect(() => {
    // Periodically check and refresh token every 30 seconds
    const interval = setInterval(refreshTokenIfNeeded, 30_000);
    return () => clearInterval(interval);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: safe to ignore
  useEffect(() => {
    if (authenticated && !user && Object.keys(company).length === 0 && !authTokenBeingRefreshed) {
      setShouldFetchData(true);
    }
  }, [authTokenBeingRefreshed, authenticated]);

  useEffect(() => {
    if (token) {
      if (hasNoPermission(token)) {
        toast(
          <Stack gap={1}>
            <Typography variant="subtitle2">Permissions Required</Typography>

            <Typography variant="body2">
              You might experience limited functionality because you are not an admin or manager. Please have your
              BuilderPad admin grant you the permissions required to use the application.
            </Typography>

            <Divider />

            <Typography variant="body2">Logout and login again to resolve this error.</Typography>

            <Button onClick={logout} variant="contained" color="error">
              Logout
            </Button>
          </Stack>,
          {
            type: 'error'
          }
        );
      }
    }
  }, [toast, token]);

  const updateToken = async () => {
    await renewSession(true, authAPI.refreshToken);
    setToken(localStorage.getItem('__token'));
    setRefreshToken(localStorage.getItem('__refresh-token'));
    setSessionRecovering(isSessionRecovering());
  };

  useEffect(() => {
    const identity = () =>
      JSON.stringify([
        localStorage.getItem('__session-generation'),
        localStorage.getItem('__company_id'),
        localStorage.getItem('__user_id'),
        localStorage.getItem('__impersonation')
      ]);
    let lastIdentity = identity();
    const syncSession = (event: Event) => {
      const nextIdentity = identity();
      if (event.type === 'storage' && nextIdentity !== lastIdentity) {
        deleteWebSocket();
        queryClient.clear();
        persister.removeClient();
        setCompany({});
        window.dispatchEvent(new Event('user-localstorage-changed'));
        if (process.env.NODE_ENV !== 'test') {
          window.location.reload();
        }
      }
      lastIdentity = nextIdentity;
      setToken(localStorage.getItem('__token'));
      setRefreshToken(localStorage.getItem('__refresh-token'));
      setSessionRecovering(isSessionRecovering());
      setAuthTokenBeingRefreshed(isSessionRenewing());
      if (!localStorage.getItem('__token')) {
        queryClient.clear();
        persister.removeClient();
        setCompany({});
      }
    };
    window.addEventListener(SESSION_EVENT, syncSession);
    window.addEventListener('storage', syncSession);
    window.addEventListener('user-localstorage-changed', syncSession);
    return () => {
      window.removeEventListener(SESSION_EVENT, syncSession);
      window.removeEventListener('storage', syncSession);
      window.removeEventListener('user-localstorage-changed', syncSession);
    };
  }, []);

  const fetchCompanyData = async () => {
    await companyAPI.fetch(company.id!).then((res) => {
      const companyInfo = res.data;
      setCompany({
        ...company!,
        company_flags: companyInfo.company_flags,
        address: companyInfo.address?.address_1 ?? '',
        address_1: companyInfo.address?.address_1 ?? '',
        address_2: companyInfo.address?.address_2 ?? '',
        city: companyInfo.address?.city ?? '',
        state: companyInfo.address?.state ?? '',
        country: companyInfo.address?.country ?? null,
        zip_code: companyInfo.address?.zip_code ?? '',
        phone: companyInfo.address?.phone ?? '',
        website: companyInfo.website,
        email: companyInfo.email,
        ein: companyInfo.ein,
        logo: companyInfo.logo,
        integration_data: companyInfo.integration || companyInfo.integration_data || company.integration_data,
        integrations_available: {
          ...(companyInfo.integration || companyInfo.integrations_available || company.integrations_available),
          procore_available: false,
          procore_timecard_available: false
        }
      });
    });
  };
  const switchCompany = async (companyId: number) => {
    const { data } = await userAPI.switchCompany(companyId);

    const decoded = jwtDecode<{ company_id?: number; user_id?: number }>(data.access);
    const nextCompanyId = decoded?.company_id?.toString() ?? '';
    const companyChanged = localStorage.getItem('__company_id') !== nextCompanyId;

    advanceSessionGeneration();
    localStorage.setItem('__token', data.access);
    localStorage.setItem('__refresh-token', data.refresh);
    localStorage.setItem('__company_id', nextCompanyId);
    localStorage.setItem('__user_id', decoded?.user_id?.toString() ?? '');

    if (companyChanged) {
      deleteWebSocket();
    }

    window.dispatchEvent(new Event('user-localstorage-changed'));

    queryClient.clear();
    persister.removeClient();

    setToken(data.access);
    setRefreshToken(data.refresh);
  };

  const [impersonation, setImpersonation] = useState<ImpersonationSession | null>(() => getImpersonationSession());

  /** Reset every client-side cache so nothing from the previous identity survives. */
  const resetClientState = (accessToken: string | null) => {
    deleteWebSocket();
    window.dispatchEvent(new Event('user-localstorage-changed'));
    queryClient.clear();
    persister.removeClient();
    setToken(accessToken);
    setRefreshToken(localStorage.getItem('__refresh-token'));
  };

  const startImpersonation = async (userId: number, reason: string) => {
    const { data } = await impersonationAPI.start(userId, reason);

    advanceSessionGeneration();
    beginImpersonation(data);
    setImpersonation(getImpersonationSession());
    resetClientState(data.access);

    // A full reload is the honest way to re-derive everything from the new identity:
    // the app holds user and company state in many places and this is rare enough
    // that correctness beats smoothness.
    window.location.assign('/');
  };

  const stopImpersonation = async () => {
    const session = getImpersonationSession();

    // Best effort: the session should be closed server side, but a failure here must
    // not strand the operator in the impersonated session.
    try {
      await impersonationAPI.end(session?.sessionId);
    } catch (error) {
      Sentry.captureMessage('Failed to end impersonation session server side', { extra: { error } });
    }

    advanceSessionGeneration();
    const restored = restoreOperatorSession();
    setImpersonation(null);
    resetClientState(localStorage.getItem('__token'));

    if (!restored) {
      // Nothing to go back to; a fresh login is the only safe outcome.
      return logout();
    }

    window.location.assign('/');
  };

  const updateUser = async (id: number, data: Partial<User>) => {
    setError(null);

    return updateUserMutation.mutateAsync({ id, data }).catch((err) => {
      setError(err);
    });
  };

  //should be used to update the currentUser / application user
  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  const updateCurrentUser = async (id: any, data: any) => {
    return updateCurrentUserMutation.mutateAsync({ id, data }).catch((err) => {
      setError(err);
    });
  };

  const updatePassword = async (id: number, data: { password: string; password_confirm: string }) => {
    return userAPI.setPassword(id, data).catch((err) => setError(err));
  };

  const createUser = async (data: Partial<User>) => {
    return userAPI.createUser(data).then(async (res) => {
      await userAPI.createProfile({
        user: res.data.id,
        company_id: data!.profile!.company!.id
      });

      return res;
    });
  };

  // biome-ignore lint/suspicious/noExplicitAny: safe to ignore
  const updateProfile = async (id: any, data: any) => {
    return updateProfileMutation.mutateAsync({ id, data }).catch((err) => {
      setError(err);
    });
  };

  const loginAfterMfa = (access: string, refresh: string) => {
    const decoded = jwtDecode<{ company_id?: number; user_id?: number }>(access);

    advanceSessionGeneration();
    setSessionRecovering(false);
    localStorage.setItem('__token', access);
    localStorage.setItem('__refresh-token', refresh);
    localStorage.setItem('__company_id', decoded?.company_id?.toString() ?? '');
    localStorage.setItem('__user_id', decoded?.user_id?.toString() ?? '');

    window.dispatchEvent(new Event('user-localstorage-changed'));

    // Without this, logging in as a different user in the same browser (without an
    // explicit logout in between, e.g. after a token expiry) would keep serving the
    // previous session's cached /me, groups, and user-list data -- queryKeys.me and
    // queryKeys.groups are not scoped by user id, and the query cache is persisted
    // to localStorage, so stale identity/permission data could survive for up to
    // an hour. logout() and switchCompany() already clear the cache for the same
    // reason; login was the one auth transition that didn't.
    queryClient.clear();
    persister.removeClient();

    setToken(access);
    setRefreshToken(refresh);
  };

  const refetchUserInfo = async () => {
    refetchMe();
  };

  const logout = async () => {
    const access = localStorage.getItem('__token');
    const logoutRequest = authAPI.logout(access);
    advanceSessionGeneration();
    const logoutSession = sessionSnapshot();
    deleteWebSocket();
    const completion = logoutRequest
      .catch(() => undefined)
      .then(() => {
        const current = sessionSnapshot();
        // Rotation retains generation; logout must clear its rotated pair too.
        if (current.generation !== logoutSession.generation || current.impersonation !== logoutSession.impersonation) {
          return;
        }
        advanceSessionGeneration();
        localStorage.removeItem('__token');
        localStorage.removeItem('__refresh-token');
        localStorage.removeItem('__company_id');
        localStorage.removeItem('__company_name');
        localStorage.removeItem('__user_id');
        localStorage.removeItem('__user_email');
        window.dispatchEvent(new Event('user-localstorage-changed'));
        queryClient.clear();
        persister.removeClient();
        const endingSession = sessionSnapshot();
        // Do not clear a replacement login's persisted cache after the throttled save settles.
        setTimeout(() => {
          if (isCurrentSession(endingSession)) {
            persister.removeClient();
          }
        }, 1100);
        setToken(null);
        setRefreshToken(null);
        setSessionRecovering(false);
        setCompany({});
        setError(null);
        resetFeaturebase();
      });
    deferRenewalDuringLogout(logoutSession.generation, completion);
    await completion;
  };

  const integrationAvailable = useMemo(
    () => user?.profile?.company?.integration || user?.profile?.company?.integrations_available || {},
    [user]
  );

  return (
    <AuthContext.Provider
      value={{
        generalSettings,

        isSidebarCondensed,
        setIsSidebarCondensed,

        user,
        userNavigator,
        groups: groupsData ?? [],
        loading,
        started,
        error,
        setError,

        authTokenRefreshChecking,

        company,
        fetchCompanyData,
        switchCompany,

        token,
        refreshToken,
        updateToken,

        isAuthenticated,
        loginAfterMfa,
        logout,

        impersonation,
        startImpersonation,
        stopImpersonation,

        createUser,
        updateUser,
        refetchUserInfo,
        updatePassword,

        updateProfile,
        updateCurrentUser,

        integrationAvailable
      }}>
      <div style={{ display: 'contents' }} {...(sessionRecovering ? { inert: '' } : {})}>
        {children}
      </div>
      {sessionRecovering && (
        <Dialog
          open
          fullScreen
          disableEscapeKeyDown
          transitionDuration={0}
          aria-labelledby="session-recovery-title"
          aria-describedby="session-recovery-description"
          sx={{ zIndex: 2000 }}
          PaperProps={{ sx: { bgcolor: 'rgba(255,255,255,0.85)' } }}>
          <Stack role="alert" sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }} gap={2}>
            <Typography id="session-recovery-title">
              We could not renew your session. Your unsaved work is still here.
            </Typography>
            <Typography id="session-recovery-description">Check your connection and retry.</Typography>
            <Button autoFocus variant="contained" onClick={refreshTokenIfNeeded}>
              Retry
            </Button>
            <Button onClick={logout}>Log out</Button>
          </Stack>
        </Dialog>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
